import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useScroll,
    useTransform,
    AnimatePresence,
} from "framer-motion";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import MaskedLines, { EASE } from "@/components/MaskedLines";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import ProjectCard from "@/components/ProjectCard";
import CTAButton from "@/components/CTAButton";
import Socials from "@/components/Socials";
import Marquee from "@/components/Marquee";
import { useContent } from "@/context/ContentContext";
import { HERO_IMAGES, ABOUT_IMAGE, RESUME_URL } from "@/data/content";

function Hero() {
    const [idx, setIdx] = useState(0);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

    const prev = () =>
        setIdx((i) => (i + HERO_IMAGES.length - 1) % HERO_IMAGES.length);
    const next = () => setIdx((i) => (i + 1) % HERO_IMAGES.length);

    const explore = () => {
        const el = document.getElementById("about");
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el);
        else el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            ref={ref}
            className="relative flex min-h-[calc(100vh-4rem)] lg:min-h-screen flex-col overflow-hidden"
            data-testid="hero-section"
        >
            <div className="flex items-center justify-between px-5 lg:px-12 xl:px-16 pt-24 lg:pt-12">
                <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6, ease: EASE }}
                    className="mono text-xs tracking-[0.3em] text-muted2"
                >
                    HI, I'M
                </motion.p>
                <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.6, ease: EASE }}
                    className="mono hidden md:block text-[11px] tracking-[0.3em] text-muted2"
                >
                    IDEAS → ACTION → IMPACT
                </motion.p>
            </div>

            <div className="grid flex-1 lg:grid-cols-[1.1fr_0.9fr] items-stretch gap-8 lg:gap-0">
                <div className="flex flex-col justify-center px-5 lg:px-12 xl:px-16 pb-12 lg:py-8">
                    <MaskedLines
                        lines={["DEVANG", "AGARWAL"]}
                        className="font-display leading-[0.88] tracking-tight text-[clamp(3.4rem,10vw,8.5rem)] block"
                        delay={0.2}
                        stagger={0.13}
                    />
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65, duration: 0.7, ease: EASE }}
                        className="mono text-xs md:text-sm tracking-[0.25em] mt-6"
                        data-testid="hero-roles"
                    >
                        BBA GRADUATE / MARKETER / BUILDER
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.78, duration: 0.7, ease: EASE }}
                        className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-muted2"
                    >
                        {useContent().hero_bio}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
                        className="mt-10"
                    >
                        <CTAButton onClick={explore} testid="hero-explore-btn">
                            EXPLORE MY WORK
                        </CTAButton>
                    </motion.div>
                </div>

                <div className="relative min-h-[340px] lg:min-h-0 overflow-hidden border-y lg:border-y-0 lg:border-l border-line">
                    <motion.div
                        style={{ y }}
                        className="absolute -top-[10%] left-0 h-[120%] w-full"
                    >
                        <AnimatePresence>
                            <motion.img
                                key={idx}
                                src={HERO_IMAGES[idx]}
                                alt="Devang's workspace"
                                className="absolute inset-0 h-full w-full object-cover"
                                initial={{ opacity: 0, scale: 1.05 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, ease: EASE }}
                            />
                        </AnimatePresence>
                    </motion.div>
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-paper">
                        <span
                            className="mono text-xs tracking-[0.25em]"
                            data-testid="hero-counter"
                        >
                            {String(idx + 1).padStart(2, "0")} /{" "}
                            {String(HERO_IMAGES.length).padStart(2, "0")}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={prev}
                                data-testid="hero-prev-btn"
                                aria-label="Previous image"
                                className="grid h-10 w-10 place-items-center rounded-full border border-paper/40 transition-colors hover:bg-paper hover:text-ink"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </button>
                            <button
                                onClick={next}
                                data-testid="hero-next-btn"
                                aria-label="Next image"
                                className="grid h-10 w-10 place-items-center rounded-full border border-paper/40 transition-colors hover:bg-paper hover:text-ink"
                            >
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function AboutPreview() {
    const content = useContent();
    return (
        <section
            id="about"
            className="px-5 lg:px-12 xl:px-16 py-20 lg:py-28"
            data-testid="about-preview-section"
        >
            <SectionHeading number="01" title="ABOUT ME" />
            <div className="mt-12 grid gap-10 lg:grid-cols-[1.1fr_0.75fr_1fr]">
                <Reveal>
                    <p className="text-base md:text-lg leading-relaxed">
                        {content.about_p1}
                    </p>
                    <p className="mt-6 text-base md:text-lg leading-relaxed text-muted2">
                        {content.about_p2}
                    </p>
                    <a
                        href={RESUME_URL}
                        data-testid="download-resume"
                        className="group inline-flex items-center gap-2 mt-8 font-semibold underline underline-offset-8 decoration-sage decoration-2 hover:text-muted2 transition-colors"
                    >
                        Download Resume
                        <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                    </a>
                </Reveal>
                <Reveal
                    delay={0.1}
                    className="overflow-hidden border border-line"
                >
                    <img
                        src={ABOUT_IMAGE}
                        alt="Coffee and notebook on Devang's desk"
                        loading="lazy"
                        className="h-full w-full object-cover aspect-[4/5] transition-transform duration-700 hover:scale-105"
                        data-testid="about-preview-image"
                    />
                </Reveal>
                <Reveal delay={0.15}>
                    <div
                        className="h-full bg-sagepanel border border-line p-8"
                        data-testid="goals-panel"
                    >
                        <p className="mono text-xs tracking-[0.3em]">
                            MY GOALS ————
                        </p>
                        <ol className="mt-6">
                            {content.goals.map((g, i) => (
                                <li
                                    key={i}
                                    className="flex gap-5 border-b last:border-0 border-ink/15 py-4"
                                >
                                    <span className="mono text-sm pt-0.5">
                                        0{i + 1}
                                    </span>
                                    <p className="text-sm md:text-base leading-snug">
                                        {g}
                                    </p>
                                </li>
                            ))}
                        </ol>
                        <p className="serif-it text-xl mt-6">
                            Big dreams. Practical steps.
                        </p>
                    </div>
                    <Link
                        to="/about"
                        data-testid="about-more-link"
                        className="group mono text-[11px] tracking-[0.25em] inline-flex items-center gap-2 mt-6 border-b border-ink/40 pb-1 hover:border-ink"
                    >
                        MORE ABOUT ME
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

function ProjectsBand() {
    const content = useContent();
    return (
        <section
            className="bg-ink2 text-paper px-5 lg:px-12 xl:px-16 py-20 lg:py-28"
            data-testid="featured-projects-section"
        >
            <div className="flex flex-wrap items-end justify-between gap-6">
                <SectionHeading number="02" title="FEATURED PROJECTS" dark />
                <Link
                    to="/projects"
                    data-testid="view-all-projects"
                    className="group mono text-[11px] tracking-[0.25em] inline-flex items-center gap-2 border-b border-paper/40 pb-1 transition-colors hover:border-sage hover:text-sage"
                >
                    VIEW ALL PROJECTS
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
            </div>
            <div className="mt-12 grid gap-8 sm:grid-cols-2 xl:grid-cols-4">
                {content.projects.map((p, i) => (
                    <Reveal key={p.slug} delay={i * 0.07}>
                        <ProjectCard project={p} i={i} dark />
                    </Reveal>
                ))}
            </div>
        </section>
    );
}


function ExperiencePreview() {
    const content = useContent();
    const entries = content.experiences || [];
    const [activeIndex, setActiveIndex] = useState(0);

    const activeExperience = entries[activeIndex];

    const prev = () => {
        setActiveIndex((current) =>
            (current - 1 + entries.length) % entries.length
        );
    };

    const next = () => {
        setActiveIndex((current) =>
            (current + 1) % entries.length
        );
    };

    return (
        <section
            className="px-5 lg:px-12 xl:px-16 py-20 lg:py-28"
            data-testid="experience-preview-section"
        >
            <SectionHeading number="03" title="EXPERIENCE" />

            {entries.length > 0 ? (
                <>
                    <div className="mt-12">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeExperience?.id || activeIndex}
                                initial={{ opacity: 0, x: 35 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -35 }}
                                transition={{ duration: 0.35, ease: "easeInOut" }}
                                className="grid gap-10 lg:grid-cols-2"
                            >
                                <Reveal>
                                    <h3 className="font-display text-2xl md:text-3xl leading-tight">
                                        {activeExperience.role}
                                    </h3>

                                    <p className="mono text-xs tracking-[0.2em] text-muted2 mt-4">
                                        {(activeExperience.org || "").toUpperCase()}
                                        {activeExperience.location
                                            ? ` — ${activeExperience.location.toUpperCase()}`
                                            : ""}
                                    </p>

                                    <p className="mono text-xs tracking-[0.2em] text-muted2 mt-1">
                                        {(activeExperience.period || "").toUpperCase()}
                                    </p>
                                </Reveal>

                                <Reveal delay={0.1}>
                                    <ul className="border-t border-line">
                                        {(activeExperience.bullets || []).map((bullet, index) => (
                                            <li
                                                key={index}
                                                className="flex gap-4 items-start border-b border-line py-4"
                                            >
                                                <span className="mt-2 h-2 w-2 shrink-0 rotate-45 bg-sage" />
                                                <p className="text-base md:text-lg leading-snug">
                                                    {bullet}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </Reveal>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="flex items-center justify-between mt-10">
                        <p className="mono text-[10px] tracking-[0.2em] text-muted2">
                            {String(activeIndex + 1).padStart(2, "0")} / {String(entries.length).padStart(2, "0")}
                        </p>

                        {entries.length > 1 && (
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={prev}
                                    aria-label="Previous experience"
                                    className="border border-line p-3 hover:bg-paper2 transition-colors"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </button>

                                <button
                                    type="button"
                                    onClick={next}
                                    aria-label="Next experience"
                                    className="border border-line p-3 hover:bg-paper2 transition-colors"
                                >
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <p
                    className="mono text-sm text-muted2 mt-12"
                    data-testid="experience-empty-state"
                >
                    Experience coming soon.
                </p>
            )}

            <Reveal>
                <Link
                    to="/experience"
                    data-testid="experience-more-link"
                    className="group mono text-[11px] tracking-[0.25em] inline-flex items-center gap-2 mt-12 border-b border-ink/40 pb-1 hover:border-ink"
                >
                    VIEW FULL TIMELINE
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
            </Reveal>
        </section>
    );
}
function SkillsPreview() {
    const content = useContent();
    return (
        <section
            className="px-5 lg:px-12 xl:px-16 py-20 lg:py-28 border-t border-line"
            data-testid="skills-preview-section"
        >
            <SectionHeading number="04" title="SKILLS" />
            <div className="mt-12 grid gap-10 md:grid-cols-3">
                {content.skills.map((group, gi) => (
                    <Reveal key={group.label} delay={gi * 0.08}>
                        <p className="mono text-[11px] tracking-[0.25em] text-muted2">
                            0{gi + 1} — {group.label.toUpperCase()}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2.5">
                            {group.items.map((s) => (
                                <span
                                    key={s}
                                    className="border border-line bg-paper2 rounded-full px-4 py-2 text-sm transition-colors duration-300 hover:bg-sage hover:text-ink hover:border-sage"
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function ContactStrip() {
    const content = useContent();
    return (
        <section
            className="px-5 lg:px-12 xl:px-16 py-20 lg:py-28 border-t border-line"
            data-testid="contact-strip-section"
        >
            <div className="flex flex-col justify-between gap-12 lg:flex-row lg:items-end">
                <div>
                    <SectionHeading number="05" title="GET IN TOUCH" />
                    <Reveal delay={0.1}>
                        <p className="mt-6 max-w-md text-muted2 text-base md:text-lg leading-relaxed">
                            I'm always open to new opportunities, collaborations
                            and interesting conversations.
                        </p>
                        <a
                            href={`mailto:${content.contact.email}`}
                            data-testid="contact-strip-email"
                            className="font-display text-2xl md:text-4xl tracking-tight mt-8 inline-block transition-colors hover:text-muted2 break-all"
                        >
                            {content.contact.email}
                        </a>
                        <div className="mt-8">
                            <Socials />
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.2} className="lg:text-right">
                    <Link
                        to="/contact"
                        data-testid="contact-strip-cta"
                        className="group inline-flex items-center gap-5"
                    >
                        <span className="serif-it text-3xl md:text-5xl leading-tight">
                            Let's build something great.
                        </span>
                        <span className="grid place-items-center h-14 w-14 shrink-0 rounded-full border border-ink transition-colors duration-300 group-hover:bg-ink group-hover:text-paper">
                            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </span>
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee />
            <AboutPreview />
            <ProjectsBand />
            <ExperiencePreview />
            <SkillsPreview />
            <ContactStrip />
        </>
    );
}
